import React from'react';
import ReactDOM from'react-dom/client';
import'./index.css';
import App from'./App';
// Capture the PWA `beforeinstallprompt` event as early as possible so the
// install/download buttons can offer installation reliably.
import'./services/pwaInstall';

// Suppress push notification and service worker errors (common warnings that don't affect functionality)
const originalError = console.error;
const originalWarn = console.warn;

// Override console.error to suppress known non-critical errors
console.error = (...args) => {
 const errorMessage = args[0];
 const errorString = typeof errorMessage ==='string'? errorMessage : (errorMessage && errorMessage.toString ? errorMessage.toString() :'');
 
 // Suppress push notification errors during service worker updates (handled gracefully)
 if (errorString.includes('Push subscribe error') || 
 errorString.includes('Push subscription failed') ||
 errorString.includes('Full error details') ||
 (errorString.includes('PushManager') && errorString.includes('no active Service Worker'))) {
 return;
 }
 
 // Suppress Service Worker update errors (browser-level errors during updates)
 if (errorString.includes('Failed to update a ServiceWorker') ||
 errorString.includes('InvalidStateError') ||
 (errorString.includes('ServiceWorker') && errorString.includes('invalid state'))) {
 console.log('ℹ️ Service Worker update in progress - this is normal');
 return;
 }
 
 originalError(...args);
};

// Override console.warn to suppress Service Worker warnings
console.warn = (...args) => {
 const warnMessage = args[0];
 const warnString = typeof warnMessage ==='string'? warnMessage : (warnMessage && warnMessage.toString ? warnMessage.toString() :'');
 
 // Suppress Service Worker warnings
 if (warnString.includes('ServiceWorker') && warnString.includes('invalid state')) {
 return;
 }
 
 originalWarn(...args);
};

// Suppress in unhandled rejection handler
window.addEventListener('unhandledrejection', (event) => {
 const errorMsg = event.reason?.message?.toString() || event.reason?.toString() ||'';
 
 // Suppress service worker update errors (handled gracefully)
 if (errorMsg.includes('Failed to update a ServiceWorker') || 
 errorMsg.includes('InvalidStateError') ||
 (errorMsg.includes('ServiceWorker') && errorMsg.includes('invalid state'))) {
 event.preventDefault();
 console.log('ℹ️ Service Worker update in progress - this is normal during app updates');
 return;
 }
 
 // Suppress push notification errors during updates (handled gracefully)
 if (errorMsg.includes('PushManager') && errorMsg.includes('no active Service Worker')) {
 event.preventDefault();
 console.log('ℹ️ Push subscription will retry when Service Worker is ready');
 return;
 }
});

// ============================================================================
// AUTOMATIC VERSION DETECTION AND UPDATE
// ============================================================================
// This ensures the latest app version is loaded by:
// 1. Checking current deployed version on app load
// 2. Comparing with stored version (if any)
// 3. Refreshing only if version changed (not on every load)
// 4. Preserving authentication tokens and user data
// 5. Clearing old caches automatically via service worker
// ============================================================================

(function checkAppVersion() {
 const VERSION_STORAGE_KEY ='uttaapp_build_version';
 const REFRESH_IN_PROGRESS_KEY ='uttaapp_refresh_in_progress';
 const MAX_REFRESH_RETRIES = 2;
 
 // Check if we're in the middle of a refresh cycle
 const refreshInProgress = sessionStorage.getItem(REFRESH_IN_PROGRESS_KEY);
 const refreshCount = parseInt(sessionStorage.getItem('uttaapp_refresh_count') ||'0', 10);
 
 if (refreshInProgress ==='true') {
 // Clear the flag since we've reloaded
 sessionStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
 console.log('✅ App refreshed, continuing with new version');
 return;
 }
 
 // Prevent infinite refresh loops
 if (refreshCount >= MAX_REFRESH_RETRIES) {
 console.warn('⚠️ Maximum refresh retries reached, skipping version check');
 sessionStorage.removeItem('uttaapp_refresh_count');
 return;
 }
 
 // Backup authentication data before any refresh operations
 const backupAuthData = () => {
 try {
 const authToken = localStorage.getItem('uttaapp_token');
 const authUser = localStorage.getItem('uttaapp_user');
 if (authToken && authUser) {
 sessionStorage.setItem('uttaapp_token_backup', authToken);
 sessionStorage.setItem('uttaapp_user_backup', authUser);
 console.log('🔐 Authentication data backed up');
 }
 } catch (error) {
 console.warn('⚠️ Error backing up auth data:', error);
 }
 };
 
 // Restore authentication data if needed
 const restoreAuthData = () => {
 try {
 const tokenBackup = sessionStorage.getItem('uttaapp_token_backup');
 const userBackup = sessionStorage.getItem('uttaapp_user_backup');
 
 if (tokenBackup && userBackup) {
 const existingToken = localStorage.getItem('uttaapp_token');
 const existingUser = localStorage.getItem('uttaapp_user');
 
 // Only restore if localStorage doesn't have auth data
 if (!existingToken || !existingUser) {
 console.log('🔐 Restoring authentication from backup...');
 localStorage.setItem('uttaapp_token', tokenBackup);
 localStorage.setItem('uttaapp_user', userBackup);
 console.log('✅ Authentication restored');
 }
 
 // Clean up backup
 sessionStorage.removeItem('uttaapp_token_backup');
 sessionStorage.removeItem('uttaapp_user_backup');
 }
 } catch (error) {
 console.warn('⚠️ Error restoring auth data:', error);
 }
 };
 
 // Check version and refresh if needed
 (async () => {
 try {
 // Restore auth data first (in case of previous refresh)
 restoreAuthData();
 
 // Fetch current version from server (always fresh, no cache)
 const versionUrl =`/version.json?t=${Date.now()}`;
 const response = await fetch(versionUrl, { 
 cache:'no-store',
 headers: {'Cache-Control':'no-cache, no-store, must-revalidate','Pragma':'no-cache'}
 });
 
 if (!response.ok) {
 console.warn('⚠️ Could not fetch version.json, skipping version check');
 return;
 }
 
 const versionData = await response.json();
 const currentVersion = versionData.version || versionData.hash || versionData.timestamp ||'unknown';
 const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
 
 console.log('📦 Version check:', {
 current: currentVersion,
 stored: storedVersion ||'none (first load)'});
 
 // First load on this device/session: establish the current version baseline
 // without forcing an extra reload. This removes the visible"blink"on open.
 if (!storedVersion) {
 localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
 console.log('✅ Version baseline initialized');
 return;
 }

 // If versions match, no update needed
 if (storedVersion === currentVersion) {
 console.log('✅ App is up to date');
 return;
 }
 
 // Version changed - need to refresh
 console.log('🔄 New version detected, refreshing app...');
 
 // Backup auth data before refresh
 backupAuthData();
 
 // Store new version
 localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
 
 // Mark refresh in progress
 sessionStorage.setItem(REFRESH_IN_PROGRESS_KEY,'true');
 sessionStorage.setItem('uttaapp_refresh_count', (refreshCount + 1).toString());
 
 // Clear service worker cache to ensure fresh assets
 if ('caches'in window) {
 try {
 const cacheNames = await caches.keys();
 const oldCaches = cacheNames.filter(name => name.startsWith('UttaApp-'));
 if (oldCaches.length > 0) {
 console.log('🧹 Clearing old caches:', oldCaches);
 await Promise.all(oldCaches.map(name => caches.delete(name)));
 // Wait a bit after clearing caches
 await new Promise(resolve => setTimeout(resolve, 100));
 }
 } catch (error) {
 console.warn('⚠️ Error clearing caches:', error);
 }
 }
 
 // Unregister and re-register service worker for clean update
 if ('serviceWorker'in navigator) {
 try {
 // Small delay to ensure cache clearing is complete
 await new Promise(resolve => setTimeout(resolve, 200));
 
 const registration = await navigator.serviceWorker.getRegistration();
 if (registration) {
 console.log('🔄 Unregistering old Service Worker for clean update...');
 await registration.unregister();
 console.log('✅ Old Service Worker unregistered');
 
 // Wait a bit before re-registering
 await new Promise(resolve => setTimeout(resolve, 300));
 }
 } catch (error) {
 // Silently ignore - not critical for version check
 console.log('ℹ️ Service Worker cleanup skipped');
 }
 }
 
 // Small delay to ensure operations complete
 await new Promise(resolve => setTimeout(resolve, 100));
 
 // Reload the page to fetch fresh assets
 console.log('🔄 Reloading to apply update...');
 window.location.reload();
 
 } catch (error) {
 console.error('❌ Error during version check:', error);
 // Don't refresh on error - let the app continue
 }
 })();
})();

// Add global error handler for unhandled errors
window.addEventListener('error', (event) => {
 const errorMsg = event.message?.toString() ||'';
 
 // Suppress Service Worker update errors (browser-level errors during updates)
 if (errorMsg.includes('Failed to update a ServiceWorker') || 
 errorMsg.includes('InvalidStateError') ||
 (errorMsg.includes('ServiceWorker') && errorMsg.includes('invalid state'))) {
 event.preventDefault();
 event.stopPropagation();
 console.log('ℹ️ Service Worker update in progress (suppressed browser error)');
 return false;
 }
 
 console.error('Global error caught:', {
 message: event.message,
 source: event.filename,
 line: event.lineno,
 column: event.colno,
 error: event.error,
 userAgent: navigator.userAgent,
 isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
 });
});

window.addEventListener('unhandledrejection', (event) => {
 const errorMsg = event.reason?.message?.toString() || event.reason?.toString() ||'';
 
 // Suppress service worker and push notification errors (handled gracefully)
 if (errorMsg.includes('Failed to update a ServiceWorker') || 
 errorMsg.includes('InvalidStateError') ||
 errorMsg.includes('PushManager') ||
 (errorMsg.includes('ServiceWorker') && errorMsg.includes('invalid state'))) {
 return; // Silently ignore these - they're handled elsewhere
 }
 
 console.error('Unhandled promise rejection:', {
 reason: event.reason,
 userAgent: navigator.userAgent,
 isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
 });
});

// Create Google Translate widget container globally at app root
// This ensures the widget is available from the start
if (!document.getElementById('google_translate_element')) {
 const translateElement = document.createElement('div');
 translateElement.id ='google_translate_element';
 translateElement.style.display ='none';
 translateElement.style.visibility ='hidden';
 translateElement.style.height ='0';
 translateElement.style.width ='0';
 translateElement.style.overflow ='hidden';
 translateElement.style.position ='absolute';
 translateElement.style.top ='-9999px';
 translateElement.style.left ='-9999px';
 document.body.appendChild(translateElement);
 console.log('✅ Google Translate widget container created at app root');
}

// Check if root element exists before rendering
const rootElement = document.getElementById('root');
if (!rootElement) {
 console.error('Root element not found!');
 document.body.innerHTML ='<div style="padding: 20px; text-align: center; font-family: sans-serif;"><h1>Error: Root element not found</h1><p>Please check if index.html has a div with id="root"</p></div>';
} else {
 const root = ReactDOM.createRoot(rootElement);
 root.render(
 <React.StrictMode>
 <App />
 </React.StrictMode>
 );
}

// Service Worker Registration for PWA
if ('serviceWorker'in navigator) {
 // Prevent multiple simultaneous registrations
 let registrationInProgress = false;
 let serviceWorkerRegistration = null;

 window.addEventListener('load', () => {
 // Check if version check is handling an update
 const versionCheckInProgress = sessionStorage.getItem('uttaapp_refresh_in_progress') ==='true';
 if (versionCheckInProgress) {
 console.log('ℹ️ Version update in progress, delaying Service Worker registration');
 }
 
 // Small delay to ensure version check completes first
 const delay = versionCheckInProgress ? 1000 : 300;
 
 setTimeout(async () => {
 // Check if already registered
 if (serviceWorkerRegistration) {
 console.log('PWA: Service Worker already registered, using existing registration');
 return serviceWorkerRegistration;
 }

 // Check if registration is in progress
 if (registrationInProgress) {
 console.log('PWA: Service Worker registration already in progress, waiting...');
 // Wait for existing registration
 try {
 serviceWorkerRegistration = await navigator.serviceWorker.ready;
 return serviceWorkerRegistration;
 } catch (e) {
 console.warn('PWA: Error waiting for service worker:', e);
 }
 }

 // Check for existing registration
 try {
 const existingRegistration = await navigator.serviceWorker.getRegistration();
 if (existingRegistration) {
 console.log('PWA: Found existing Service Worker registration, using it');
 serviceWorkerRegistration = existingRegistration;
 registrationInProgress = false;
 
 // Wait for service worker to be active
 if (existingRegistration.active) {
 console.log('PWA: Service Worker is active');
 } else if (existingRegistration.installing || existingRegistration.waiting) {
 console.log('PWA: Service Worker is installing/waiting, waiting for activation...');
 await navigator.serviceWorker.ready;
 console.log('PWA: Service Worker is now ready');
 }
 // Continue with setup using existing registration
 } else {
 registrationInProgress = true;
 // Register service worker.
 // IMPORTANT: tie the SW script URL to the deployed app version so clients reliably pick up SW changes.
 // This prevents cases where an older SW keeps controlling the page (and can break Google Translate script loads).
 const appVersionForSw =
 localStorage.getItem('uttaapp_build_version') ||
 sessionStorage.getItem('uttaapp_build_version') ||'v1';
 const swUrl =`/sw.js?v=${encodeURIComponent(appVersionForSw)}`;
 serviceWorkerRegistration = await navigator.serviceWorker.register(swUrl, { 
 updateViaCache:'none',
 scope:'/'});
 
 // Wait for service worker to be ready
 await navigator.serviceWorker.ready;
 registrationInProgress = false;
 console.log('PWA: Service Worker registered and ready');
 }
 } catch (error) {
 registrationInProgress = false;
 console.error('PWA: Service Worker registration failed:', error);
 // Suppress error - don't block app from loading
 return;
 }

 const registration = serviceWorkerRegistration;
 
 // Continue with the rest of the setup
 if (registration) {
 // Check for updates periodically and on focus (with throttling)
 let updateInProgress = false;
 let lastUpdateCheck = 0;
 const UPDATE_INTERVAL = 60000; // Minimum 60 seconds between update checks
 
 const checkForUpdates = async () => {
 const now = Date.now();
 if (updateInProgress || (now - lastUpdateCheck < UPDATE_INTERVAL)) {
 return; // Skip if update in progress or too soon
 }
 
 try {
 updateInProgress = true;
 lastUpdateCheck = now;
 await registration.update();
 console.log('✅ Service Worker update check completed');
 } catch (e) {
 // Silently ignore update errors
 console.log('ℹ️ Service Worker update check skipped');
 } finally {
 updateInProgress = false;
 }
 };
 
 // Check for updates when page becomes visible (throttled)
 document.addEventListener('visibilitychange', () => {
 if (!document.hidden) {
 checkForUpdates();
 }
 });
 
 // Check for updates when window regains focus (throttled)
 window.addEventListener('focus', checkForUpdates);
 
 // Skip initial update check (version check already handled it)
 console.log('ℹ️ Skipping initial SW update check (version check handled it)');

 // Handle waiting service worker
 if (registration.waiting) {
 console.log('PWA: Service worker update waiting, activating...');
 registration.waiting.postMessage({ type:'SKIP_WAITING'});
 }

 // Listen for new service worker installation
 registration.addEventListener('updatefound', () => {
 const newWorker = registration.installing;
 if (!newWorker) return;

 console.log('PWA: New service worker found, installing...');

 newWorker.addEventListener('statechange', () => {
 if (newWorker.state ==='installed') {
 if (navigator.serviceWorker.controller) {
 // There's an existing service worker, activate the new one
 console.log('PWA: New service worker installed, activating...');
 newWorker.postMessage({ type:'SKIP_WAITING'});
 } else {
 // First time installation
 console.log('PWA: Service worker installed for the first time');
 }
 }
 });
 });

 // Listen for service worker activation messages
 navigator.serviceWorker.addEventListener('message', (event) => {
 if (event.data && event.data.type ==='SW_ACTIVATED') {
 console.log('PWA: Service worker activated with version:', event.data.version);
 // Version check will handle reload if needed
 }
 });

 // Handle controller change (new service worker took control)
 // Do not force a second reload here; the version check above already
 // reloads only when there is a real deployed version change.
 let controllerChangeHandled = false;
 navigator.serviceWorker.addEventListener('controllerchange', () => {
 if (controllerChangeHandled) return;

 controllerChangeHandled = true;
 console.log('✅ Service worker controller changed; continuing without forced reload');
 });
 
 // Initialize push notifications after service worker is ready
 if ('PushManager'in window &&'Notification'in window) {
 // Wait for service worker to be ready and active
 navigator.serviceWorker.ready.then(async (reg) => {
 try {
 // Ensure service worker is active before attempting push subscription
 if (!reg.active) {
 console.log('⏳ Waiting for service worker to activate...');
 await new Promise((resolve) => {
 const checkActive = () => {
 if (reg.active) {
 resolve();
 } else {
 setTimeout(checkActive, 100);
 }
 };
 checkActive();
 });
 }
 
 console.log('✅ Service Worker is active, proceeding with push subscription...');
 await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
 
 const webPushService = (await import('./services/webPushService')).default;
 
 // Check current permission status
 const currentPermission = Notification.permission;
 console.log('🔔 Initial notification permission status:', currentPermission);
 
 if (currentPermission ==='default') {
 // Request permission immediately
 console.log('📱 Requesting notification permission on app load...');
 const permission = await webPushService.requestPermission();
 
 if (permission ==='granted') {
 console.log('✅ Permission granted, subscribing to push...');
 // Subscribe after permission is granted
 webPushService.subscribe().then(result => {
 if (result.success) {
 console.log('✅ Push subscription successful on app load');
 } else {
 console.warn('⚠️ Push subscription failed:', result.error);
 }
 }).catch(error => {
 console.warn('⚠️ Push subscription error (will retry later):', error.message || error);
 });
 }
 } else if (currentPermission ==='granted') {
 // Permission already granted, ensure subscription
 console.log('✅ Permission already granted, ensuring subscription...');
 webPushService.subscribe().then(result => {
 if (result.success) {
 console.log('✅ Push subscription verified/created');
 } else {
 console.warn('⚠️ Push subscription check failed (will retry later):', result.error);
 }
 }).catch(error => {
 console.warn('⚠️ Push subscription error (will retry later):', error.message || error);
 });
 } else {
 console.log('⚠️ Notification permission denied');
 }
 } catch (error) {
 console.warn('⚠️ Error initializing push notifications (will retry later):', error.message || error);
 }
 }).catch(err => {
 console.warn('⚠️ Service Worker not ready for push notifications:', err.message || err);
 });
 }
 }
 }, delay);
 });
}

